import type {TrackDispatcher} from '@gaia/hooks/useTracks';
import createAsyncMutex from '@gaia/utils/createAsyncMutex';
import debounceWithResolver from '@gaia/utils/debounceWithResolver';
import fetchJSON from '@gaia/utils/fetchJSON';
import {getTrackStats} from '@gaia/utils/gaia/getTrackStats';
import isEqual from 'lodash/isEqual';
import map from 'lodash/map';
import omit from 'lodash/omit';

import fetchElevation from './fetchElevation';
import fetchTrackMultiline from './fetchTrackMultiLine';
import saveWaypoint from './saveWaypoint';
import snapTrackPoint from './snapTrackPoint';

const debouncedFetch = debounceWithResolver(window.fetch, 500);

export const savedTracks: Record<string, Track> = {};

export function getIsTrackModifiedSinceSave(track: Track) {
    const savedTrack = savedTracks[track.properties.localId];
    const omitProps = [
        'properties.id',
        'properties.localId',
        'properties.distance',
        'properties.total_ascent',
        'properties.total_descent',
        'properties.isLocallyCreated',
        'properties.isLatestImport',
    ];

    if (!savedTrack && track.properties.deleted) return false;
    else if (!savedTrack) return true;
    else if (isEqual(omit(savedTrack, omitProps), omit(track, omitProps))) return false;
    else if (savedTrack.properties.deleted) return false;
    else return true;
}

export function registerSavedTrack(track: Track) {
    savedTracks[track.properties.localId] = track;
}

export default createAsyncMutex(
    async (track: Track, project: MapProject, trackDispatcher: TrackDispatcher): Promise<void> => {
        const isModifiedSinceSave = getIsTrackModifiedSinceSave(track);
        if (!isModifiedSinceSave) return;
        registerSavedTrack(track);

        const elevatedGeometry = await fetchElevation(await fetchTrackMultiline({track, project}));

        // Track stats are added to the track asynchronously server side,
        // so they are not available on the response object
        const {distance, ascent, descent} = await getTrackStats(elevatedGeometry);

        const body = JSON.stringify({
            type: 'Feature',
            properties: {
                ...track.properties,
                hexcolor: track.properties.color,
            },
            geometry: elevatedGeometry,
        });

        if (track.properties.id) {
            await debouncedFetch(`/api/objects/track/${track.properties.id}/`, {
                method: 'PUT',
                credentials: 'same-origin',
                headers: {'Content-Type': 'application/json'},
                body,
            });
            await saveTrackWaypoints(track.properties.id, track, project, trackDispatcher);
            trackDispatcher({
                type: 'updateProperties',
                properties: {
                    localId: track.properties.localId,
                    distance,
                    total_ascent: ascent,
                    total_descent: descent,
                },
            });
        } else {
            const response = await fetchJSON('/api/objects/track/', {
                method: 'POST',
                credentials: 'same-origin',
                headers: {'Content-Type': 'application/json'},
                body,
            });
            const id = response.features[0].properties.id;
            trackDispatcher({
                type: 'updateProperties',
                properties: {
                    id,
                    localId: track.properties.localId,
                    distance,
                    total_ascent: ascent,
                    total_descent: descent,
                },
            });
            await saveTrackWaypoints(id, track, project, trackDispatcher);
        }
    },
    (track) => track.properties.localId
);

async function saveTrackWaypoints(
    trackId: string,
    track: Track,
    project: MapProject,
    trackDispatcher: TrackDispatcher
) {
    if (!track.isGeometryLoaded) return;

    return Promise.all(
        map(track.points, async (point) =>
            saveWaypoint(
                {
                    ...point,
                    properties: {
                        ...point.properties,
                        track_id: trackId,
                    },
                    geometry: await snapTrackPoint({
                        point,
                        track,
                        project,
                        interaction: undefined,
                    }),
                },
                (id) =>
                    trackDispatcher({
                        type: 'updatePointProperties',
                        properties: {
                            id,
                            track_id: trackId,
                            localId: point.properties.localId,
                            trackLocalId: track.properties.localId,
                        },
                    })
            )
        )
    );
}
